import { Vector2 } from '@babylonjs/core/Maths/math';
import remove from 'lodash/remove';
import find from 'lodash/find';
import filter from 'lodash/filter';

import { MouseCatcher, NakerMouseEvent } from '../Catchers/mouseCatcher';
import { ScrollCatcher } from '../Catchers/scrollCatcher';
import { ProgressEvent } from '../Catchers/progressCatcher';
import { ContentAnimation, IAnimationOptions } from './contentAnimation';
import { ContentModel } from './contentModel';
import { ContentLight } from './contentLight';

const getProgressAnimation = (contentAnim: IAnimationOptions, top: number): number => {
    if (top < contentAnim.delay) {
        if (contentAnim.started) {
            contentAnim.finished = false;
            contentAnim.started = false;
            return 0;
        }
    } else if (top > contentAnim.duration) {
        if (!contentAnim.finished) {
            contentAnim.started = true;
            contentAnim.finished = true;
            return 1;
        }
    } else {
        // Make sure we don't divide by 0
        if (contentAnim.duration === 0) return null;
        contentAnim.launched = true;
        contentAnim.started = true;
        contentAnim.finished = false;
        const perc = (top - contentAnim.delay) / (contentAnim.duration - contentAnim.delay);
        return perc;
    }
    return null;
};

export class ContentsTrigger {
    public list: ContentAnimation[] = [];

    private scrollPercentage = 0;

    private mouseCatcher: MouseCatcher;

    private scrollCatcher: ScrollCatcher;

    constructor(mouseCatcher: MouseCatcher, scrollCatcher?: ScrollCatcher) {
        this.mouseCatcher = mouseCatcher;

        if (scrollCatcher) {
            this.scrollCatcher = scrollCatcher;

            this.scrollCatcher.on(ProgressEvent.Progress, (ScrollData) => {
                if (this.scrollEvent) this.scrollContents(ScrollData.progress);
                // Make sure scrollPercentage is setted for mouse animation
                this.scrollPercentage = ScrollData.progress;
            });
        }
    }

    public _addContent(content: ContentAnimation): void {
        if (this.list.indexOf(content) === -1) this.list.push(content);
    }

    public _removeContent(content: ContentAnimation): void {
        remove(this.list, (o) => o.id === content.id);
    }

    private scrollContents(top: number) {
        this.list.forEach((content) => {
            if (content.play) {
                const animKeys = Object.keys(content.animations);
                animKeys.forEach((key) => {
                    const animation = content.animations[key];
                    if (animation.event === 'scroll' && !animation.forcestop) {
                        const progress = getProgressAnimation(animation, top);
                        content.animateScroll(animation, progress);
                    }
                });
            }
        });
    }

    private mouseVectorRatio = new Vector2(1, -1);

    private mousemoveContents(mousepos: Vector2) {
        const mouseChange = mousepos.multiply(this.mouseVectorRatio);
        this.list.forEach((content) => {
            if (content.play) {
                const animKeys = Object.keys(content.animations);
                animKeys.forEach((key) => {
                    const animation = content.animations[key];
                    if (animation.event === 'mouse' && !animation.forcestop) {
                        const progress = getProgressAnimation(animation, this.scrollPercentage);
                        if (progress !== null) {
                            content.animateMouse(animation, mouseChange.x, mouseChange.y);
                        }
                    }
                });
            }
        });
    }

    public checkIfAnimations(): void {
        let scrollNeed = false; let
            mouseNeed = false;
        this.list.forEach((content) => {
            if (content.play) {
                const animKeys = Object.keys(content.animations);
                animKeys.forEach((key) => {
                    const animation = content.animations[key];
                    if (animation.event === 'mouse') mouseNeed = true;
                    if (animation.event === 'scroll') scrollNeed = true;
                });
            }
        });
        this.setMouseEvent(mouseNeed);
        this.setScrollEvent(scrollNeed);
    }

    private setMouseEvent(mouseEvent: boolean) {
        // Can't use anonymous function or this is not available
        // Instead observable scope is important to keep it working
        if (mouseEvent) {
            this.mouseCatcher.on(NakerMouseEvent.Move, this.mousemoveContents, this);
        } else {
            this.mouseCatcher.off(NakerMouseEvent.Move, this.mousemoveContents);
        }
    }

    private scrollEvent = false;

    private setScrollEvent(scrollEvent: boolean) {
        this.scrollEvent = scrollEvent;
    }

    public getContentByTag(tag: string): ContentAnimation {
        return find(this.list, (o) => o.tag === tag);
    }

    public getContentByType(type: string): ContentAnimation[] {
        return filter(this.list, (o) => o.type === type);
    }

    public getContentModels(): ContentModel[] {
        return this.getContentByType('model') as ContentModel[];
    }

    public getContentLights(): ContentLight[] {
        return this.getContentByType('light') as ContentLight[];
    }

    public getFirstModel(): ContentModel {
        return this.getContentModels()[0] as ContentModel;
    }

    public hasOnePlayableModel(): boolean {
        let hasAnimation = false;
        const models = this.getContentModels();
        models.forEach((model) => {
            if (model.hasModelAnimation()) hasAnimation = true;
        });
        return hasAnimation;
    }

    public playModelAnimations(): void {
        const models = this.getContentModels();
        models.forEach((model) => {
            model.playFirstAnimation();
        });
    }

    public pauseModelAnimations(): void {
        const models = this.getContentModels();
        models.forEach((model) => {
            model.stopAllModelAnimations();
        });
    }
}
